import React from 'react';
import Tippy from '@tippyjs/react/headless';
import { Button } from '../../Button/Button';
import { ButtonGroup } from '../../ButtonGroup/ButtonGroup';
import { Calendar, CalendarProps } from '../Calendar/Calendar';
import { Box } from '../../Box/Box';
import { Card } from '../../Card/Card';
import { Icon } from '../../Icon/Icon';
import { ListItem } from '../../List/ListItem';
import { Input } from '../../Input/Input';
import { CUSTOM_RANGE_LABEL, DateRange, useDateRangePicker } from './useDateRangePicker';
import { DATE_FORMAT, dateToString } from '../utils';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../Menu/Menu';

interface DateRangePickerProps extends Omit<CalendarProps, 'onChange' | 'isRange'> {
  range: DateRange;
  onChange(range: DateRange): void;
  presets?: DateRange[];
  isReadonly?: boolean;
  isVerticalLayout?: boolean;
  id?: string;
  'aria-label'?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  range,
  onChange,
  presets,
  isReadonly,
  minDate,
  maxDate,
  isVerticalLayout,
  id = '',
  'aria-label': ariaLabel,
  ...rest
}) => {
  const { state, refs, handlers, helpers } = useDateRangePicker({
    range,
    minDate,
    maxDate,
    onChange,
    presets,
  });

  return (
    <ButtonGroup
      isAttached={!isVerticalLayout}
      isVertical={isVerticalLayout}
      {...(isVerticalLayout ? { spacing: 16 } : {})}
      role="group"
      aria-label={ariaLabel}
    >
      {presets && presets.length > 0 && (
        <Menu>
          <MenuTrigger
            onClick={() => {
              if (refs.calendarInstance) {
                refs.calendarInstance.hide();
              }
            }}
          >
            <Button
              variant="secondary"
              rightIcon="fas fa-caret-down"
              css={isVerticalLayout ? 'font-weight: 400; justify-content:space-between' : ''}
            >
              {state.presetLabel}
            </Button>
          </MenuTrigger>
          <MenuContent align="start">
            {presets.map(
              preset =>
                preset.label && (
                  <MenuItem
                    key={preset.label}
                    onSelect={() => {
                      onChange(preset);
                      handlers.onInternalChange(preset);
                    }}
                  >
                    <ListItem
                      icon="far fa-check"
                      variant={preset.label === state.presetLabel ? 'selected' : 'inactive'}
                    >
                      {preset.label}
                    </ListItem>
                  </MenuItem>
                ),
            )}

            {!isReadonly && (
              <MenuItem
                onSelect={() => {
                  if (refs.calendarInstance) {
                    refs.calendarInstance.show();
                  }
                }}
              >
                <ListItem
                  icon="far fa-check"
                  variant={state.presetLabel === CUSTOM_RANGE_LABEL ? 'selected' : 'inactive'}
                >
                  Custom Range
                </ListItem>
              </MenuItem>
            )}
          </MenuContent>
        </Menu>
      )}

      <Tippy
        interactive
        trigger={isReadonly ? 'none' : 'click'}
        placement="bottom-end"
        onCreate={refs.setCalendarInstance}
        onClickOutside={() => {
          state.setInternalRange(range);

          helpers.rerenderCalendar();
        }}
        appendTo={document.body}
        zIndex={20000}
        offset={[0, 4]}
        render={attrs => (
          <Card {...attrs} boxShadow="500" hasBorder width={296} p="24">
            <Box display="flex" flexDirection="row" alignItems="center" mb="12">
              <Input
                type="text"
                value={state.fromDate}
                onChange={handlers.onFromDateChange}
                onBlur={helpers.fromDateValidation}
                onKeyDown={evt => handlers.onInputKeydown(evt, 'fromDate')}
                placeholder={DATE_FORMAT}
                textAlign="center"
                aria-label="From date"
              />

              <Icon className="far fa-arrow-right" color="neutral.600" mx="8" fontSize="body" />

              <Input
                type="text"
                value={state.toDate}
                onChange={handlers.onToDateChange}
                onBlur={helpers.toDateValidation}
                onKeyDown={evt => handlers.onInputKeydown(evt, 'toDate')}
                placeholder={DATE_FORMAT}
                textAlign="center"
                aria-label="To date"
              />
            </Box>

            <Calendar
              key={state.calendarKey}
              isRange
              value={state.internalRange.dates as [Date | null, Date | null]}
              onChange={dates => handlers.onInternalChange({ ...state.internalRange, dates })}
              minDate={minDate}
              maxDate={maxDate}
              {...rest}
            />

            <Box display="flex" flexDirection="row" justifyContent="flex-end" mt="24">
              <Button
                variant="subtleInactive"
                onClick={() => {
                  if (refs.calendarInstance) {
                    refs.calendarInstance.hide();

                    state.setInternalRange(range);

                    helpers.rerenderCalendar();
                  }
                }}
              >
                Cancel
              </Button>
              <Button onClick={handlers.commit} ml="8" ref={refs.submitRef}>
                Apply
              </Button>
            </Box>
          </Card>
        )}
      >
        <Button
          variant="secondary"
          leftIcon="far fa-calendar-alt"
          tabIndex={isReadonly ? -1 : 0}
          style={{ pointerEvents: isReadonly ? 'none' : 'auto', fontWeight: 400 }}
          disabled={isReadonly}
          css={isVerticalLayout ? 'font-weight: 400; width: 100%; justify-content: flex-start' : ''}
        >
          {`${dateToString(range.dates[0])} - ${dateToString(range.dates[1])}`}
        </Button>
      </Tippy>
    </ButtonGroup>
  );
};
